import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { countOutboxEntries } from "../offline/outbox";
import { processOutbox, registerBackgroundSync, OUTBOX_SYNC_TAG } from "../offline/syncEngine";
import { isBrowserOnline } from "../offline/network";

const OfflineSyncContext = createContext(null);

export function OfflineSyncProvider({ children }) {
  const queryClient = useQueryClient();
  const [pendingCount, setPendingCount] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [lastSyncAt, setLastSyncAt] = useState(null);
  const [lastSyncError, setLastSyncError] = useState(null);
  const [isOnline, setIsOnline] = useState(() => isBrowserOnline());

  const refreshPendingCount = useCallback(async () => {
    const n = await countOutboxEntries();
    setPendingCount(n);
    return n;
  }, []);

  const invalidateTeacherQueries = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["attendanceSubmissions"] });
    queryClient.invalidateQueries({ queryKey: ["marksSubmissions"] });
    queryClient.invalidateQueries({ queryKey: ["classStudents"] });
    queryClient.invalidateQueries({ queryKey: ["schoolNotices"] });
  }, [queryClient]);

  const runSync = useCallback(async () => {
    if (!isBrowserOnline() || syncing) return { processed: 0, failed: 0 };
    setSyncing(true);
    setLastSyncError(null);
    try {
      const result = await processOutbox();
      await refreshPendingCount();
      if (result.processed > 0) {
        invalidateTeacherQueries();
        setLastSyncAt(new Date().toISOString());
      }
      return result;
    } catch (err) {
      setLastSyncError(err?.message || "Sync failed");
      throw err;
    } finally {
      setSyncing(false);
    }
  }, [syncing, refreshPendingCount, invalidateTeacherQueries]);

  const queueMutation = useCallback(async () => {
    await refreshPendingCount();
    await registerBackgroundSync();
  }, [refreshPendingCount]);

  useEffect(() => {
    refreshPendingCount();
  }, [refreshPendingCount]);

  useEffect(() => {
    const onOnline = () => {
      setIsOnline(true);
      runSync();
    };
    const onOffline = () => setIsOnline(false);

    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [runSync]);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return undefined;

    const onMessage = (event) => {
      if (event.data?.type === "PROCESS_OUTBOX") {
        runSync();
      }
    };

    navigator.serviceWorker.addEventListener("message", onMessage);
    return () => navigator.serviceWorker.removeEventListener("message", onMessage);
  }, [runSync]);

  useEffect(() => {
    if (isOnline) runSync();
  }, [isOnline, runSync]);

  const value = useMemo(
    () => ({
      isOnline,
      pendingCount,
      syncing,
      lastSyncAt,
      lastSyncError,
      runSync,
      queueMutation,
      refreshPendingCount,
    }),
    [
      isOnline,
      pendingCount,
      syncing,
      lastSyncAt,
      lastSyncError,
      runSync,
      queueMutation,
      refreshPendingCount,
    ]
  );

  return (
    <OfflineSyncContext.Provider value={value}>{children}</OfflineSyncContext.Provider>
  );
}

export function useOfflineSync() {
  const ctx = useContext(OfflineSyncContext);
  if (!ctx) {
    throw new Error("useOfflineSync must be used within OfflineSyncProvider");
  }
  return ctx;
}

export { OUTBOX_SYNC_TAG };
